import { useMemo } from 'react';

import path from 'path';

import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import ErrorPage from '~components/errorBoundary/errorPage';
import ErrorType from '~components/errorBoundary/errorType';
import { pagePathSegments, queryParameters } from '~constants';
import OrganisationsService from '~features/organisations/services/organisationsService';
import PageLayout from '~layouts/page/pageLayout';
import { getService } from '~services/serviceFactory';

import DeviceGroupDetailsContainer from '../containers/deviceGroupDetailsContainer';
import DeviceGroupProvider from '../providers/deviceGroupProvider';
import DeviceGroupService from '../services/deviceGroupService';

const deviceGroupService = getService(DeviceGroupService);
const organisationsService = getService(OrganisationsService);

const DeviceGroupDetailsPage = () => {
	const { t } = useTranslation();
	const { id: deviceGroupId } = useParams();
	const [searchParams] = useSearchParams();
	const overwrittenClientId = searchParams.get(queryParameters.OverwrittenClientId) ?? undefined;

	const {
		data: deviceGroup,
		isLoading,
		error,
	} = useSWR(
		deviceGroupService.getDeviceGroupByIdCacheKey(deviceGroupId, overwrittenClientId),
		([_, deviceGroupId, overwrittenClientId]) => {
			if (!deviceGroupId) return undefined;
			return deviceGroupService.getDeviceGroupById({
				deviceGroupId: deviceGroupId,
				overwrittenClientId,
			});
		}
	);

	const { data: organisation, isLoading: isLoadingOrganisation } = useSWR(
		organisationsService.getOrganisationByIdCacheKey(overwrittenClientId),
		([_, id]) => (id ? organisationsService.getOrganisationById(id) : null)
	);

	const crumbs = useMemo(
		() => [
			...(organisation ?
				[
					{
						title: organisation?.name ?? overwrittenClientId,
						path: path.join('/', pagePathSegments.Organisations, overwrittenClientId ?? ''),
					},
				]
			:	[]),
			{
				title: t('deviceGroup.labels.plural', { ns: 'entities' }),
				path: path.join('/', pagePathSegments.DeviceGroups),
			},
			{
				title: deviceGroup?.name ?? '',
				path: '',
			},
		],
		[organisation, deviceGroup]
	);

	// --- All hooks above, all early returns below ---
	if (deviceGroupId == undefined) {
		return <Navigate to={pagePathSegments.Devices} replace />;
	}

	if (!isLoading && error) {
		return <ErrorPage type={ErrorType.NotFound} />;
	}

	return (
		<DeviceGroupProvider
			value={{
				deviceGroup: deviceGroup,
				isLoading: isLoading || isLoadingOrganisation,
				hasError: error != undefined,
				overwrittenClientId: overwrittenClientId,
			}}
		>
			<PageLayout
				pageTitle={deviceGroup?.name ?? ''}
				headerInfo={{
					title: deviceGroup?.name,
				}}
				crumbs={crumbs}
			>
				<DeviceGroupDetailsContainer />
			</PageLayout>
		</DeviceGroupProvider>
	);
};

export default DeviceGroupDetailsPage; 